import { MessageBar, MessageBarType } from "@fluentui/react";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import RadioGroupField from "components/controls/RadioGroupField";
import TextInputField from "components/controls/TextInputField";
import { constants } from "library/constants";
import {
  isDisabledExternalForAll,
  isDisabledExternalOrHasBeenActivated,
} from "pages/vehicle/helpers";
import { vehicleFormClasses } from "pages/vehicle/VehicleForm";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";
import { PremiumType } from "types/types";
import RequestedLines from "./RequestedLines";
import {
  Coverages,
  CZ_REQUESTED_LINE_FIELDS,
  parseRequestedLines,
} from "./requestedLinesHelpers";
import { getMatchedHullCalc, hasCoverageValue } from "./hullDefaultSetting";
import {
  CLIENT_HIDDEN_FINANCE_FIELDS,
  isClientFormUser,
} from "../../clientVehicleSpec";
import {
  useClientAssistanceOptions,
  useClientLineOptions,
} from "../../hooks/useClientLineOptions";

// Additional hull coverages offered to AT / CZ clients. These have NO backend
// columns, so the client's selection is stored inside the `clientCoveragesInfo`
// JSON (alongside the requested lines), not on real vehicle fields.
const ADDITIONAL_COVERAGES: (keyof Coverages)[] = [
  "gap",
  "replacementValue",
  "grossNegligence",
  "electroPlusPackage",
];
// CZ client: only GAP is offered.
const CZ_ADDITIONAL_COVERAGES: (keyof Coverages)[] = ["gap"];

const fullRowStyle = {
  width: "100%",
};

const FinanceGeneralFields = ({ formikProps, vehicle, route }) => {
  const { createTradeVehicle } = constants;

  const { t } = useTranslation();
  const userRole = useSelector((s) => s.auth.userRole);
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings); // Add this line
  const { values, errors, handleChange, setFieldValue } = formikProps;
  const taxonomy = useSelector((s) => s.taxonomy);
  const fleet = useSelector((s) => s.vehicle.fleet);
  const countryCode = fleet?.countryCode;

  // Helper function to check if field should be visible
  const isFieldVisible = (fieldName: string) => {
    // Client: hide finance fields not in the Smart Forms Excel.
    if (isClientForm && CLIENT_HIDDEN_FINANCE_FIELDS.has(fieldName)) {
      return false;
    }
    return vehicleAppSettings?.VISIBLE_FIELDS?.includes(fieldName) ?? true;
  };

  const incorrectVehicleIssuingCodeId =
    taxonomy.VehicleIssuingStatus.byCode["VehicleIssuingStatus.INCORRECT"]?.id;

  const isTradeLicensePlate =
    route === createTradeVehicle || vehicle?.isTradeLicensePlate;

  const isHullStartDateSet = !!vehicle?.hullLastStartDate;

  // --- AT / CZ client (external) offer behaviour ----------------------------
  const isAustria = vehicleAppSettings?.COUNTRY_CODE === "Country.AT";
  const isCzech = vehicleAppSettings?.COUNTRY_CODE === "Country.CZ";
  const isClientForm = isClientFormUser(vehicleAppSettings?.COUNTRY_CODE, userRole);
  // CZ: special equipment is optional for both the client and the internal user
  const isSpecialEquipmentRequired =
    !isCzech && (isHullStartDateSet || isClientForm);
  const requestedLines = parseRequestedLines(values?.clientCoveragesInfo);
  const hullRequested = requestedLines.hull;
  // For a client, hull-value fields only appear once Hull is requested.
  const showHullFields = !isClientForm || hullRequested;
  // The client may edit these on create/edit-while-pending; others keep the
  // existing (locked-for-external) behaviour.
  const clientFieldDisabled = isClientForm
    ? route === "readOnlyVehicle"
    : isDisabledExternalForAll({ vehicleAppSettings, userRole, route });

  const fleetDefaultSettings = useSelector(
    (s) => s.vehicle.fleetDefaultSettings
  );
  const getLineOptions = useClientLineOptions();
  const getAssistanceOptions = useClientAssistanceOptions();
  const hullPremiumTypeId =
    taxonomy.PremiumType?.byCode?.["PremiumType.HULL"]?.id;
  // The fleet's hull premium-calculation default setting (matched by the main
  // hull insurer + vehicle type) drives the derived list-price-type and which
  // additional coverages are offered to the client.
  const matchedHullCalc = useMemo(
    () =>
      getMatchedHullCalc({
        fleet,
        fleetDefaultSettings,
        taxonomy,
        hullPremiumTypeId,
        isTradeLicensePlate,
        vehicleValues: {
          vehicleTypeCode: values?.vehicleTypeCode?.value ?? null,
          carUsageCode: values?.carUsageCode?.value ?? null,
          truckMountingCode: values?.truckMountingCode?.value ?? null,
          powerKw: values?.powerKw ?? null,
          emptyWeightKg: values?.emptyWeightKg ?? null,
          weightCapacityKg: values?.weightCapacityKg ?? null,
          maxGrossWeightKg: values?.maxGrossWeightKg ?? null,
          cubicCapacityCcm: values?.cubicCapacityCcm ?? null,
          seats: values?.seats ?? null,
          listPrice: values?.listPrice ?? null,
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fleet,
      fleetDefaultSettings,
      taxonomy,
      hullPremiumTypeId,
      isTradeLicensePlate,
      values?.vehicleTypeCode,
      values?.carUsageCode,
      values?.truckMountingCode,
      values?.powerKw,
      values?.emptyWeightKg,
      values?.weightCapacityKg,
      values?.maxGrossWeightKg,
      values?.cubicCapacityCcm,
      values?.seats,
      values?.listPrice,
    ]
  );

  // AT client: the list price type always follows the matched hull setting
  // (read-only). CZ client: pre-filled from it when one matches, but editable -
  // the fleet may have no matching setting and the field is mandatory with Hull.
  const isListPriceTypeLocked = isClientForm && isAustria;

  const derivedListPriceTypeOption = useMemo(() => {
    const typeId = matchedHullCalc?.listPriceType;
    if (!typeId) return null;
    const item = taxonomy.ListPriceType.byId[typeId];
    return item ? { value: item.id, label: t(item.code) } : null;
  }, [matchedHullCalc, taxonomy.ListPriceType.byId, t]);

  // AT client: a coverage is offered iff it has a value in the matched setting.
  // CZ client: all coverages are always offered once Hull is requested.
  const coverageEnabled = (key: string) =>
    !isAustria || hasCoverageValue(matchedHullCalc?.calc, key as any);

  // Premium-free special equipment amount from the matched setting (info only).
  const premiumFreeSpecialEquipment =
    matchedHullCalc?.calc?.premiumFreeSpecialEquipment;
  const showPremiumFreeNote =
    isClientForm &&
    hullRequested &&
    premiumFreeSpecialEquipment != null &&
    String(premiumFreeSpecialEquipment).trim() !== "";

  // Leasing company only applies when Leasing = Yes; clear it otherwise.
  const leasingYes =
    values?.isLeasing === "true" || values?.isLeasing === true;
  useEffect(() => {
    if (isClientForm && !leasingYes && values?.leasingCompany) {
      setFieldValue("leasingCompany", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientForm, leasingYes]);

  const vehicleIssuingSubstatusOptions = useMemo(() => {
    return taxonomy.VehicleIssuingSubstatus.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.VehicleIssuingSubstatus.items]);

  const listPriceTypeCodeOptions = useMemo(() => {
    return taxonomy.ListPriceType.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.ListPriceType.items]);

  const vehicleBonusMalusCodeOptions = useMemo(() => {
    const countryObj = taxonomy.Country.byId[countryCode];
    if (!countryObj) {
      return [];
    }
    const country = countryObj.code.split(".")[1];

    const itemsByCountry = taxonomy.VehicleBonusMalusCode.items.filter(
      (item) => {
        return item.code.includes(country);
      },
    );

    return itemsByCountry.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [
    taxonomy.Country.byId,
    taxonomy.VehicleBonusMalusCode.items,
    countryCode,
    t,
  ]);

  return (
    <>
      {isClientForm && (
        <div className={vehicleFormClasses.item} style={fullRowStyle}>
          <RequestedLines
            value={values.clientCoveragesInfo}
            setFieldValue={setFieldValue}
            disabled={clientFieldDisabled}
            showCoverageDates={!isAustria}
            errors={errors}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "clientCoveragesInfo"
            )}
            offeredCoverages={(isCzech
              ? CZ_ADDITIONAL_COVERAGES
              : ADDITIONAL_COVERAGES
            ).filter(coverageEnabled)}
            lineFields={isCzech ? CZ_REQUESTED_LINE_FIELDS : undefined}
            getLineOptions={isCzech ? getLineOptions : undefined}
            getAssistanceOptions={isCzech ? getAssistanceOptions : undefined}
            showWindscreen={isCzech}
          />
        </div>
      )}
      {isFieldVisible("comment") && (
        <div className={vehicleFormClasses.item} style={fullRowStyle}>
          {/* 25 */}
          <TextInputField
            value={values.comment}
            onChange={handleChange}
            errors={errors}
            name={"comment"}
            multiline
            rows={7}
            disabled={isDisabledExternalForAll({
              vehicleAppSettings,
              userRole,
              route,
            })}
            label={t("bfm.vehicleForm.comment.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes("comment")}
          />
        </div>
      )}
      {isTradeLicensePlate && (
        <>
          {isFieldVisible("costCenterClient") && (
            <div className={vehicleFormClasses.item}>
              <TextInputField
                errors={errors}
                value={values.costCenterClient}
                onChange={handleChange}
                maxLength={254}
                name={"costCenterClient"}
                disabled={route === "readOnlyVehicle"}
                label={"bfm.vehicleForm.costCenterClient.label"}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "costCenterClient",
                )}
              />
            </div>
          )}

          {/* 24 */}
          {isFieldVisible("clientComment") && (
            <div className={vehicleFormClasses.commentItem}>
              <TextInputField
                value={values.clientComment}
                onChange={handleChange}
                errors={errors}
                name={"clientComment"}
                multiline
                rows={7}
                disabled={route === "readOnlyVehicle"}
                label={"bfm.vehicleForm.clientComment.label"}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "clientComment",
                )}
              />
            </div>
          )}

          {isFieldVisible("freeTextField1") && (
            <div className={vehicleFormClasses.commentItemGrid}>
              <TextInputField
                value={values.freeTextField1}
                onChange={handleChange}
                errors={errors}
                name={"freeTextField1"}
                multiline
                rows={7}
                disabled={route === "readOnlyVehicle"}
                label={"bfm.vehicleForm.freeTextField1.label"}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "freeTextField1",
                )}
              />
            </div>
          )}

          {isFieldVisible("freeTextField2") && (
            <div className={vehicleFormClasses.commentItemGrid}>
              <TextInputField
                value={values.freeTextField2}
                onChange={handleChange}
                errors={errors}
                name={"freeTextField2"}
                multiline
                rows={7}
                disabled={route === "readOnlyVehicle"}
                label={"bfm.vehicleForm.freeTextField2.label"}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "freeTextField2",
                )}
              />
            </div>
          )}
        </>
      )}
      {isFieldVisible("vehicleIssuingStatusCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={true}
            errors={errors}
            name="vehicleIssuingStatusCode"
            label={t("bfm.vehicleForm.vehicleIssuingStatusCode.label")}
            setFieldValue={setFieldValue}
            options={[]}
            defaultValue={values.vehicleIssuingStatusCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleIssuingStatusCode",
            )}
            confidentiality={null}
          />
        </div>
      )}
      {isFieldVisible("vehicleIssuingSubstatusCode") &&
        !!values.vehicleIssuingStatusCode &&
        values.vehicleIssuingStatusCode.value ===
          incorrectVehicleIssuingCodeId && (
          <div className={vehicleFormClasses.item}>
            <ComboBoxField
              disabled={isDisabledExternalOrHasBeenActivated({
                vehicleAppSettings,
                route,
                vehicle,
                userRole,
              })}
              errors={errors}
              required
              name="vehicleIssuingSubstatusCode"
              label={t("bfm.vehicleForm.vehicleIssuingSubstatusCode.label")}
              setFieldValue={setFieldValue}
              options={vehicleIssuingSubstatusOptions}
              defaultValue={values.vehicleIssuingSubstatusCode}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "vehicleIssuingSubstatusCode",
              )}
              confidentiality={null}
            />
          </div>
        )}
      {isFieldVisible("vehicleIssuingComment") && (
        <div className={vehicleFormClasses.item} style={fullRowStyle}>
          <TextInputField
            value={values.vehicleIssuingComment}
            onChange={handleChange}
            errors={errors}
            name={"vehicleIssuingComment"}
            multiline
            rows={7}
            disabled={true}
            label={t("bfm.vehicleForm.vehicleIssuingComment.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleIssuingComment",
            )}
          />
        </div>
      )}
      {isFieldVisible("leasingCompany") && !isClientForm && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.leasingCompany}
            onChange={handleChange}
            errors={errors}
            name={"leasingCompany"}
            disabled={clientFieldDisabled}
            label={t("bfm.vehicleForm.leasingCompany.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "leasingCompany",
            )}
          />
        </div>
      )}
      {vehicleAppSettings?.SHOW_LEASING_EXTRA_FIELDS && (
        <>
          {isFieldVisible("leasingContractNumber") && (
            <>
              <div className={vehicleFormClasses.item}>
                <TextInputField
                  value={values.leasingContractNumber}
                  onChange={handleChange}
                  errors={errors}
                  name={"leasingContractNumber"}
                  disabled={isDisabledExternalForAll({
                    vehicleAppSettings,
                    userRole,
                    route,
                  })}
                  label={t("bfm.vehicleForm.leasingContractNumber.label")}
                  isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                    "leasingContractNumber",
                  )}
                />
              </div>
              <div className={vehicleFormClasses.item}></div>
            </>
          )}

          {isFieldVisible("leasingContractStartDate") && (
            <div className={vehicleFormClasses.item}>
              <DatePickerField
                errors={errors}
                setFieldValue={setFieldValue}
                defaultValue={values.leasingContractStartDate}
                name={"leasingContractStartDate"}
                disabled={isDisabledExternalForAll({
                  vehicleAppSettings,
                  userRole,
                  route,
                })}
                label={"bfm.vehicleForm.leasingContractStartDate.label"}
                placeholder={t("bfm.selectDate")}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "leasingContractStartDate",
                )}
                confidentiality={null}
              />
            </div>
          )}

          {isFieldVisible("leasingContractEndDate") && (
            <div className={vehicleFormClasses.item}>
              <DatePickerField
                errors={errors}
                setFieldValue={setFieldValue}
                defaultValue={values.leasingContractEndDate}
                name={"leasingContractEndDate"}
                disabled={isDisabledExternalForAll({
                  vehicleAppSettings,
                  userRole,
                  route,
                })}
                label={"bfm.vehicleForm.leasingContractEndDate.label"}
                placeholder={t("bfm.selectDate")}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "leasingContractEndDate",
                )}
                confidentiality={null}
              />
            </div>
          )}

          {(isFieldVisible("leasingAmountPaidByLessee") ||
            isFieldVisible("leasingAmountPaidByLesseeEur")) &&
            (vehicleAppSettings?.HAS_ANNUAL_PREMIUM_EUR ? (
              <div
                className={vehicleFormClasses.item}
                style={{ display: "flex", gap: "15px" }}
              >
                {isFieldVisible("leasingAmountPaidByLessee") && (
                  <div style={{ flex: 1 }}>
                    <DoubleNumberInputField
                      setFieldValue={setFieldValue}
                      value={values.leasingAmountPaidByLessee}
                      onChange={handleChange}
                      errors={errors}
                      name={"leasingAmountPaidByLessee"}
                      disabled={isDisabledExternalForAll({
                        vehicleAppSettings,
                        userRole,
                        route,
                      })}
                      label={t(
                        "bfm.vehicleForm.leasingAmountPaidByLessee.label",
                      )}
                      isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                        "leasingAmountPaidByLessee",
                      )}
                    />
                  </div>
                )}
                {isFieldVisible("leasingAmountPaidByLesseeEur") && (
                  <div style={{ flex: 1 }}>
                    <DoubleNumberInputField
                      setFieldValue={setFieldValue}
                      value={values.leasingAmountPaidByLesseeEur}
                      onChange={handleChange}
                      errors={errors}
                      name={"leasingAmountPaidByLesseeEur"}
                      disabled={isDisabledExternalForAll({
                        vehicleAppSettings,
                        userRole,
                        route,
                      })}
                      label={t(
                        "bfm.vehicleForm.leasingAmountPaidByLesseeEur.label",
                      )}
                      isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                        "leasingAmountPaidByLesseeEur",
                      )}
                    />
                  </div>
                )}
              </div>
            ) : (
              isFieldVisible("leasingAmountPaidByLessee") && (
                <div className={vehicleFormClasses.item}>
                  <DoubleNumberInputField
                    setFieldValue={setFieldValue}
                    value={values.leasingAmountPaidByLessee}
                    onChange={handleChange}
                    errors={errors}
                    name={"leasingAmountPaidByLessee"}
                    disabled={isDisabledExternalForAll({
                      vehicleAppSettings,
                      userRole,
                      route,
                    })}
                    label={t("bfm.vehicleForm.leasingAmountPaidByLessee.label")}
                    isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                      "leasingAmountPaidByLessee",
                    )}
                  />
                </div>
              )
            ))}
        </>
      )}
      {isFieldVisible("eurotaxCode") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.eurotaxCode}
            onChange={handleChange}
            errors={errors}
            name={"eurotaxCode"}
            disabled={isDisabledExternalForAll({
              vehicleAppSettings,
              userRole,
              route,
            })}
            label={t("bfm.vehicleForm.eurotaxCode.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "eurotaxCode",
            )}
          />
        </div>
      )}
      {isFieldVisible("leasingCompanyAddress") &&
        (vehicleAppSettings?.HAS_ADDITIONAL_LEASING_FIELDS ? (
          <div className={vehicleFormClasses.item}>
            <TextInputField
              value={values.leasingCompanyAddress}
              onChange={handleChange}
              errors={errors}
              name={"leasingCompanyAddress"}
              disabled={isDisabledExternalForAll({
                vehicleAppSettings,
                userRole,
                route,
              })}
              label={t("bfm.vehicleForm.leasingCompanyAddress.label")}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "leasingCompanyAddress",
              )}
            />
          </div>
        ) : (
          <div className={vehicleFormClasses.item}></div>
        ))}
      {(isFieldVisible("loanAmount") || isFieldVisible("loanAmountEur")) &&
        (vehicleAppSettings?.HAS_LOAN_AMOUNT_FIELDS ? (
          <div
            className={vehicleFormClasses.item}
            style={{ display: "flex", gap: "15px" }}
          >
            {isFieldVisible("loanAmount") && (
              <div style={{ flex: 1 }}>
                <DoubleNumberInputField
                  setFieldValue={setFieldValue}
                  value={values.loanAmount}
                  onChange={handleChange}
                  errors={errors}
                  name={"loanAmount"}
                  disabled={isDisabledExternalForAll({
                    vehicleAppSettings,
                    userRole,
                    route,
                  })}
                  label={t("bfm.vehicleForm.loanAmount.label")}
                  isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                    "loanAmount",
                  )}
                />
              </div>
            )}
            {isFieldVisible("loanAmountEur") && (
              <div style={{ flex: 1 }}>
                <DoubleNumberInputField
                  setFieldValue={setFieldValue}
                  value={values.loanAmountEur}
                  onChange={handleChange}
                  errors={errors}
                  name={"loanAmountEur"}
                  disabled={isDisabledExternalForAll({
                    vehicleAppSettings,
                    userRole,
                    route,
                  })}
                  label={t("bfm.vehicleForm.loanAmountEur.label")}
                  isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                    "loanAmountEur",
                  )}
                />
              </div>
            )}
          </div>
        ) : (
          <div className={vehicleFormClasses.item}></div>
        ))}

      {isFieldVisible("bonusMalusCode") && (
        <>
          <div className={vehicleFormClasses.item}>
            <ComboBoxField
              disabled={isDisabledExternalForAll({
                vehicleAppSettings,
                userRole,
                route,
              })}
              errors={errors}
              name="bonusMalusCode"
              label={t("bfm.vehicleForm.bonusMalusCode.label")}
              setFieldValue={setFieldValue}
              options={vehicleBonusMalusCodeOptions || []}
              defaultValue={values.bonusMalusCode}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "bonusMalusCode",
              )}
              confidentiality={null}
            />
          </div>

          <div className={vehicleFormClasses.item}></div>
        </>
      )}
      {isFieldVisible("leasingCompanyTaxCode") &&
        (vehicleAppSettings?.HAS_ADDITIONAL_LEASING_FIELDS ? (
          <div className={vehicleFormClasses.item}>
            <TextInputField
              value={values.leasingCompanyTaxCode}
              onChange={handleChange}
              errors={errors}
              name={"leasingCompanyTaxCode"}
              disabled={isDisabledExternalForAll({
                vehicleAppSettings,
                userRole,
                route,
              })}
              label={t("bfm.vehicleForm.leasingCompanyTaxCode.label")}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "leasingCompanyTaxCode",
              )}
            />
          </div>
        ) : (
          <div className={vehicleFormClasses.item}></div>
        ))}
      {isFieldVisible("listPrice") &&
        showHullFields &&
        (vehicleAppSettings?.HAS_ANNUAL_PREMIUM_EUR ? (
          <div
            className={vehicleFormClasses.item}
            style={{ display: "flex", gap: "15px" }}
          >
            <div style={{ flex: 1 }}>
              <DoubleNumberInputField
                setFieldValue={setFieldValue}
                value={values.listPrice}
                onChange={handleChange}
                errors={errors}
                required={isHullStartDateSet || isClientForm}
                name={"listPrice"}
                disabled={clientFieldDisabled}
                label={t("bfm.vehicleForm.listPrice.label")}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "listPrice",
                )}
              />
            </div>
            {isFieldVisible("listPriceEur") && (
              <div style={{ flex: 1 }}>
                <DoubleNumberInputField
                  setFieldValue={setFieldValue}
                  value={values.listPriceEur}
                  onChange={handleChange}
                  errors={errors}
                  name={"listPriceEur"}
                  disabled={clientFieldDisabled}
                  label={t("bfm.vehicleForm.listPriceEur.label")}
                  isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                    "listPriceEur",
                  )}
                />
              </div>
            )}
          </div>
        ) : (
          <div className={vehicleFormClasses.item}>
            <DoubleNumberInputField
              setFieldValue={setFieldValue}
              value={values.listPrice}
              onChange={handleChange}
              errors={errors}
              required={isHullStartDateSet || isClientForm}
              name={"listPrice"}
              disabled={clientFieldDisabled}
              label={t("bfm.vehicleForm.listPrice.label")}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "listPrice",
              )}
            />
          </div>
        ))}
      {isFieldVisible("listPriceTypeCode") && showHullFields && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={
              isListPriceTypeLocked ||
              (isClientForm
                ? clientFieldDisabled
                : isDisabledExternalForAll({
                    vehicleAppSettings,
                    userRole,
                    route,
                  }))
            }
            errors={errors}
            required={isHullStartDateSet || isClientForm}
            name="listPriceTypeCode"
            label={t("bfm.vehicleForm.listPriceTypeCode.label")}
            setFieldValue={setFieldValue}
            options={listPriceTypeCodeOptions}
            defaultValue={
              isListPriceTypeLocked
                ? derivedListPriceTypeOption
                : isClientForm
                  ? (derivedListPriceTypeOption ?? values.listPriceTypeCode)
                  : values.listPriceTypeCode
            }
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "listPriceTypeCode",
            )}
            confidentiality={null}
          />
          {isClientForm && hullRequested && (
            <MessageBar messageBarType={MessageBarType.info} isMultiline>
              {t("bfm.vehicleForm.listPriceImportant.note")}
            </MessageBar>
          )}
        </div>
      )}
      {vehicleAppSettings?.HAS_SPECIAL_EQUIPMENT_LIST_FIELD ? (
        <>
          <div className={vehicleFormClasses.item}></div>
          {isFieldVisible("specialEquipment") && showHullFields && (
            <div className={vehicleFormClasses.item}>
              <DoubleNumberInputField
                setFieldValue={setFieldValue}
                value={values.specialEquipment}
                onChange={handleChange}
                errors={errors}
                required={isSpecialEquipmentRequired}
                name={"specialEquipment"}
                disabled={clientFieldDisabled}
                label={t("bfm.vehicleForm.specialEquipment.label")}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "specialEquipment",
                )}
              />
              {showPremiumFreeNote && (
                <MessageBar messageBarType={MessageBarType.info} isMultiline>
                  {t("bfm.vehicleForm.premiumFreeSpecialEquipment.note", {
                    amount: premiumFreeSpecialEquipment,
                  })}
                </MessageBar>
              )}
            </div>
          )}

          {isFieldVisible("specialEquipmentList") && (
            <div className={vehicleFormClasses.item}>
              <TextInputField
                value={values.specialEquipmentList}
                onChange={handleChange}
                errors={errors}
                name={"specialEquipmentList"}
                multiline
                rows={7}
                disabled={isDisabledExternalForAll({
                  vehicleAppSettings,
                  userRole,
                  route,
                })}
                label={t("bfm.vehicleForm.specialEquipmentList.label")}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "specialEquipmentList",
                )}
              />
            </div>
          )}
          <div className={vehicleFormClasses.item}></div>
        </>
      ) : (
        isFieldVisible("specialEquipment") &&
        showHullFields && (
          <div className={vehicleFormClasses.item} style={fullRowStyle}>
            <DoubleNumberInputField
              setFieldValue={setFieldValue}
              value={values.specialEquipment}
              onChange={handleChange}
              errors={errors}
              required={isSpecialEquipmentRequired}
              name={"specialEquipment"}
              disabled={clientFieldDisabled}
              label={t("bfm.vehicleForm.specialEquipment.label")}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "specialEquipment",
              )}
            />
            {showPremiumFreeNote && (
              <MessageBar messageBarType={MessageBarType.info} isMultiline>
                {t("bfm.vehicleForm.premiumFreeSpecialEquipment.note", {
                  amount: premiumFreeSpecialEquipment,
                })}
              </MessageBar>
            )}
          </div>
        )
      )}
      {isFieldVisible("isIncludingTax") && showHullFields && (
        <div className={vehicleFormClasses.item}>
          <RadioGroupField
            checked={values?.isIncludingTax}
            errors={errors}
            setFieldValue={setFieldValue}
            required={isHullStartDateSet || isClientForm}
            hideNotDefined={isClientForm}
            name={"isIncludingTax"}
            disabled={clientFieldDisabled}
            label={t("bfm.vehicleForm.isIncludingTax.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "isIncludingTax",
            )}
          />
        </div>
      )}
      {/* Not gated behind the client's hull selection: VAT reimbursement is
          relevant without hull cover too (e.g. an MTPL claim where the other
          party is at fault), so it is mandatory for everyone in AT and for
          the CZ client. */}
      {isFieldVisible("vatReimbursment") && (
        <div className={vehicleFormClasses.item}>
          <RadioGroupField
            checked={values?.vatReimbursment}
            errors={errors}
            setFieldValue={setFieldValue}
            required={isAustria || isClientForm || isHullStartDateSet}
            hideNotDefined={isAustria || isClientForm}
            name={"vatReimbursment"}
            disabled={clientFieldDisabled}
            label={t("bfm.vehicleForm.vatReimbursment.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vatReimbursment",
            )}
          />
        </div>
      )}
      {isFieldVisible("isLeasing") && showHullFields && (
        <div className={vehicleFormClasses.item}>
          <RadioGroupField
            checked={values?.isLeasing}
            errors={errors}
            setFieldValue={setFieldValue}
            required={isHullStartDateSet || isClientForm}
            hideNotDefined={isClientForm}
            name={"isLeasing"}
            disabled={clientFieldDisabled}
            label={t("bfm.vehicleForm.isLeasing.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "isLeasing",
            )}
          />
        </div>
      )}
      {isClientForm && hullRequested && leasingYes && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.leasingCompany}
            onChange={handleChange}
            errors={errors}
            name={"leasingCompany"}
            disabled={clientFieldDisabled}
            label={t("bfm.vehicleForm.leasingCompany.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "leasingCompany",
            )}
          />
        </div>
      )}
      {vehicleAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
        PremiumType.MACHINERY_BREAKDOWN_PRORATA,
      ) ||
      vehicleAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
        PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE,
      ) ? (
        <>
          {isFieldVisible("machineryBreakdownListPrice") && (
            <div className={vehicleFormClasses.item}>
              <DoubleNumberInputField
                setFieldValue={setFieldValue}
                value={values.machineryBreakdownListPrice}
                onChange={handleChange}
                errors={errors}
                name={"machineryBreakdownListPrice"}
                disabled={isDisabledExternalForAll({
                  vehicleAppSettings,
                  userRole,
                  route,
                })}
                label={t("bfm.vehicleForm.machineryBreakdownListPrice.label")}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "machineryBreakdownListPrice",
                )}
              />
            </div>
          )}

          {isFieldVisible("machineryBreakdownListPriceTypeCode") && (
            <div className={vehicleFormClasses.item}>
              <ComboBoxField
                disabled={isDisabledExternalForAll({
                  vehicleAppSettings,
                  userRole,
                  route,
                })}
                errors={errors}
                name="machineryBreakdownListPriceTypeCode"
                label={t(
                  "bfm.vehicleForm.machineryBreakdownListPriceTypeCode.label",
                )}
                setFieldValue={setFieldValue}
                options={listPriceTypeCodeOptions}
                defaultValue={values.machineryBreakdownListPriceTypeCode}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "machineryBreakdownListPriceTypeCode",
                )}
                confidentiality={null}
              />
            </div>
          )}

          {isFieldVisible("machineryBreakdownSpecialEquipment") && (
            <div className={vehicleFormClasses.item}>
              <DoubleNumberInputField
                setFieldValue={setFieldValue}
                value={values.machineryBreakdownSpecialEquipment}
                onChange={handleChange}
                errors={errors}
                name={"machineryBreakdownSpecialEquipment"}
                disabled={isDisabledExternalForAll({
                  vehicleAppSettings,
                  userRole,
                  route,
                })}
                label={t(
                  "bfm.vehicleForm.machineryBreakdownSpecialEquipment.label",
                )}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "machineryBreakdownSpecialEquipment",
                )}
              />
            </div>
          )}

          {isFieldVisible("machineryBreakdownIsIncludingTax") && (
            <div className={vehicleFormClasses.item}>
              <RadioGroupField
                checked={values?.machineryBreakdownIsIncludingTax}
                errors={errors}
                setFieldValue={setFieldValue}
                name={"machineryBreakdownIsIncludingTax"}
                disabled={isDisabledExternalForAll({
                  vehicleAppSettings,
                  userRole,
                  route,
                })}
                label={t(
                  "bfm.vehicleForm.machineryBreakdownIsIncludingTax.label",
                )}
                isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                  "machineryBreakdownIsIncludingTax",
                )}
              />
            </div>
          )}
        </>
      ) : null}
    </>
  );
};

export default FinanceGeneralFields;
